import { logClientAuditEvent } from '../../project/services/auditTrailApi';
import type { Device } from '../types/device';

const DEVICE_MODULE = 'DEVICE';
const ENTITY_TYPE = 'DEVICE';

function serializeAuditValue(value: unknown): unknown {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'object') {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function buildDeviceOperationBeforeState(params: {
  device: Device;
  deviceTime?: unknown;
  deviceSetTime?: unknown;
  logCount?: unknown;
  weldParameterLogs?: unknown;
  eraseLogs?: unknown;
}): Record<string, unknown> {
  const { device } = params;

  return {
    deviceId: device.id,
    deviceStatus: device.status,
    communicationStatus: device.communication_status ?? null,
    weldMachineConnected: device.weldMachineConnected ?? null,
    weldType: device.metadata?.weldType ?? null,
    weldUID: device.metadata?.weldUID ?? null,
    ipAddress: device.metadata?.ipAddress ?? device.deviceData?.ipAddress ?? device.deviceData?.ip_address ?? null,
    projectId: device.project?.id ?? null,
    projectName: device.project?.name ?? device.project?.projectName ?? null,
    projectNumber: device.project?.projectNumber ?? null,
    deviceTime: serializeAuditValue(params.deviceTime ?? device.get_time ?? null),
    setTime: serializeAuditValue(params.deviceSetTime ?? device.set_time ?? null),
    logCount: serializeAuditValue(params.logCount ?? device.number_of_logs?.data ?? null),
    weldParameterLogs: serializeAuditValue(params.weldParameterLogs ?? device.weld_parameter_logs ?? null),
    eraseLogs: serializeAuditValue(params.eraseLogs ?? device.erase_logs ?? null),
  };
}

function logDeviceAudit(params: {
  eventType: string;
  deviceId: string;
  projectId?: number | null;
  projectName?: string | null;
  actionDetails: Record<string, unknown>;
  severity?: 'INFO' | 'WARN' | 'ERR';
  summary?: string;
  displayTitle?: string;
}): void {
  logClientAuditEvent({
    eventType: params.eventType,
    module: DEVICE_MODULE,
    entityType: ENTITY_TYPE,
    entityId: params.deviceId,
    entityName: params.deviceId,
    projectId: params.projectId ?? undefined,
    projectName: params.projectName ?? undefined,
    severity: params.severity ?? 'INFO',
    summary: params.summary,
    displayTitle: params.displayTitle ?? params.deviceId,
    actionDetails: params.actionDetails,
  });
}

export function logDeviceProjectAssigned(params: {
  device: Device;
  newProjectId: number;
  newProjectName: string;
  newProjectNumber?: string | number | null;
  assignedBy?: string;
  bulk?: boolean;
}): void {
  const previousProject = params.device.project;
  const isChange = Boolean(previousProject?.id || previousProject?.name);
  const eventType = params.bulk
    ? 'DEVICE_PROJECT_BULK_ASSIGNED'
    : isChange
      ? 'DEVICE_PROJECT_CHANGED'
      : 'DEVICE_PROJECT_ASSIGNED';

  logDeviceAudit({
    eventType,
    deviceId: params.device.id,
    projectId: params.newProjectId,
    projectName: params.newProjectName,
    summary: isChange
      ? `Device ${params.device.id} moved to ${params.newProjectName}.`
      : `Device ${params.device.id} assigned to ${params.newProjectName}.`,
    actionDetails: {
      before: buildDeviceOperationBeforeState({ device: params.device }),
      deviceId: params.device.id,
      operation: isChange ? 'CHANGE' : 'ASSIGN',
      assignedBy: params.assignedBy ?? null,
      previousProjectId: previousProject?.id ?? null,
      previousProjectName: previousProject?.name ?? null,
      previousProjectNumber: previousProject?.projectNumber ?? null,
      newProjectId: params.newProjectId,
      newProjectName: params.newProjectName,
      newProjectNumber: params.newProjectNumber ?? null,
    },
  });
}

export function logDeviceBulkProjectAssigned(params: {
  devices: Device[];
  newProjectId: number;
  newProjectName: string;
  newProjectNumber?: string | number | null;
  assignedBy?: string;
}): void {
  logClientAuditEvent({
    eventType: 'DEVICE_PROJECT_BULK_ASSIGNED',
    module: DEVICE_MODULE,
    entityType: ENTITY_TYPE,
    entityId: 'bulk',
    entityName: params.newProjectName,
    projectId: params.newProjectId,
    projectName: params.newProjectName,
    summary: `${params.devices.length} device(s) assigned to ${params.newProjectName}.`,
    displayTitle: params.newProjectName,
    actionDetails: {
      assignedBy: params.assignedBy ?? null,
      newProjectId: params.newProjectId,
      newProjectName: params.newProjectName,
      newProjectNumber: params.newProjectNumber ?? null,
      deviceCount: params.devices.length,
      operations: params.devices.map((device) => ({
        deviceId: device.id,
        operation: device.project ? 'CHANGE' : 'ASSIGN',
        before: buildDeviceOperationBeforeState({ device }),
        previousProjectId: device.project?.id ?? null,
        previousProjectName: device.project?.name ?? null,
        previousProjectNumber: device.project?.projectNumber ?? null,
      })),
    },
  });
}

export function logDeviceRegistrationOpened(device: Device): void {
  logDeviceAudit({
    eventType: 'DEVICE_REGISTRATION_OPENED',
    deviceId: device.id,
    projectId: device.project?.id ?? null,
    projectName: device.project?.name ?? null,
    summary: `Device registration opened for ${device.id}.`,
    actionDetails: {
      before: buildDeviceOperationBeforeState({ device }),
      action: 'OPEN_DEVICE_REGISTRATION',
    },
  });
}

export function logDeviceStatusUpdated(params: {
  device: Device;
  previousStatus: string;
  newStatus: string;
}): void {
  logDeviceAudit({
    eventType: 'DEVICE_STATUS_UPDATED',
    deviceId: params.device.id,
    projectId: params.device.project?.id ?? null,
    projectName: params.device.project?.name ?? null,
    severity: params.newStatus === 'Deregistered' ? 'WARN' : 'INFO',
    summary: `Device ${params.device.id} status changed to ${params.newStatus}.`,
    actionDetails: {
      before: buildDeviceOperationBeforeState({ device: params.device }),
      previousStatus: params.previousStatus,
      newStatus: params.newStatus,
    },
  });
}

export function logDeviceWeldParamsDownloadStarted(params: {
  device: Device;
  deviceTime?: unknown;
  deviceSetTime?: unknown;
  logCount?: unknown;
  weldParameterLogs?: unknown;
  reDownload?: boolean;
  projectId?: number | null;
  projectName?: string | null;
  projectNumber?: string | number | null;
}): void {
  const projectId = params.projectId ?? params.device.project?.id ?? null;
  const projectName =
    params.projectName ??
    params.device.project?.name ??
    params.device.project?.projectName ??
    null;
  const projectNumber =
    params.projectNumber ?? params.device.project?.projectNumber ?? null;

  const before = buildDeviceOperationBeforeState({
    device: params.device,
    deviceTime: params.deviceTime,
    deviceSetTime: params.deviceSetTime,
    logCount: params.logCount,
    weldParameterLogs: params.weldParameterLogs,
  });

  const projectLabel = projectName
    ? ` on project ${projectName}`
    : projectId != null
      ? ` on project #${projectId}`
      : '';

  logDeviceAudit({
    eventType: 'DEVICE_WELD_PARAMS_DOWNLOAD_STARTED',
    deviceId: params.device.id,
    projectId,
    projectName,
    summary: `Weld parameters download started for device ${params.device.id}${projectLabel}.`,
    actionDetails: {
      before: {
        ...before,
        projectId: projectId ?? before.projectId,
        projectName: projectName ?? before.projectName,
        projectNumber: projectNumber ?? before.projectNumber,
      },
      deviceId: params.device.id,
      projectId,
      projectName,
      projectNumber,
      reDownload: params.reDownload ?? false,
      action: 'WELD_PARAMS_DOWNLOAD',
      source: params.projectId != null || params.projectName != null ? 'PROJECT_PRODUCTION' : 'DEVICE_MANAGEMENT',
    },
  });
}

export function logDeviceLogsEraseStarted(params: {
  device: Device;
  deviceTime?: unknown;
  deviceSetTime?: unknown;
  logCount?: unknown;
  eraseLogs?: unknown;
  machineSlot?: number;
  weldUID?: string;
}): void {
  const before = buildDeviceOperationBeforeState({
    device: params.device,
    deviceTime: params.deviceTime,
    deviceSetTime: params.deviceSetTime,
    logCount: params.logCount,
    eraseLogs: params.eraseLogs,
  });

  logDeviceAudit({
    eventType: 'DEVICE_LOGS_ERASE_STARTED',
    deviceId: params.device.id,
    projectId: params.device.project?.id ?? null,
    projectName: params.device.project?.name ?? null,
    severity: 'WARN',
    summary: `Device logs erase started for ${params.device.id}.`,
    actionDetails: {
      before,
      action: 'ERASE_LOGS',
      ...(params.machineSlot ? { machineSlot: params.machineSlot } : {}),
      ...(params.weldUID ? { weldUID: params.weldUID } : {}),
    },
  });
}

export function logDeviceTimeSyncStarted(params: {
  device: Device;
  requestedTime: string;
  deviceTime?: unknown;
  deviceSetTime?: unknown;
  machineSlot?: number;
  weldUID?: string;
}): void {
  const before = buildDeviceOperationBeforeState({
    device: params.device,
    deviceTime: params.deviceTime,
    deviceSetTime: params.deviceSetTime,
  });

  logDeviceAudit({
    eventType: 'DEVICE_TIME_SYNC_STARTED',
    deviceId: params.device.id,
    projectId: params.device.project?.id ?? null,
    projectName: params.device.project?.name ?? null,
    summary: `Device time sync started for ${params.device.id}.`,
    actionDetails: {
      before,
      requestedTime: params.requestedTime,
      action: 'SET_TIME',
      ...(params.machineSlot ? { machineSlot: params.machineSlot } : {}),
      ...(params.weldUID ? { weldUID: params.weldUID } : {}),
    },
  });
}

export function logDeviceGetTimeRequested(params: {
  device: Device;
  deviceTime?: unknown;
  deviceSetTime?: unknown;
  machineSlot?: number;
  weldUID?: string;
}): void {
  const before = buildDeviceOperationBeforeState({
    device: params.device,
    deviceTime: params.deviceTime,
    deviceSetTime: params.deviceSetTime,
  });

  logDeviceAudit({
    eventType: 'DEVICE_GET_TIME_REQUESTED',
    deviceId: params.device.id,
    projectId: params.device.project?.id ?? null,
    projectName: params.device.project?.name ?? null,
    summary: `Device time read requested for ${params.device.id}.`,
    actionDetails: {
      before,
      action: 'GET_TIME',
      ...(params.machineSlot ? { machineSlot: params.machineSlot } : {}),
      ...(params.weldUID ? { weldUID: params.weldUID } : {}),
    },
  });
}

export function logDeviceFirmwareUploaded(params: {
  device: Device;
  currentVersion?: string | null;
  newVersion?: string | null;
  fileName: string;
  fileSize: number;
  processType?: string | null;
  platform?: string | null;
  connectionType?: string | null;
  fileKey?: string | null;
}): void {
  const before = buildDeviceOperationBeforeState({ device: params.device });

  logDeviceAudit({
    eventType: 'DEVICE_FIRMWARE_UPLOADED',
    deviceId: params.device.id,
    projectId: params.device.project?.id ?? null,
    projectName: params.device.project?.name ?? null,
    summary: `Firmware ${params.newVersion ?? params.fileName} uploaded for ${params.device.id}.`,
    actionDetails: {
      before,
      action: 'FIRMWARE_UPLOAD',
      deviceId: params.device.id,
      currentVersion: params.currentVersion ?? params.device.EdgeFWVersion ?? null,
      newVersion: params.newVersion ?? null,
      fileName: params.fileName,
      fileSize: params.fileSize,
      processType: params.processType ?? null,
      platform: params.platform ?? null,
      connectionType: params.connectionType ?? null,
      fileKey: params.fileKey ?? null,
    },
  });
}

export function logDeviceFirmwareUpdateStarted(params: {
  device: Device;
  deviceIds: string[];
  currentVersion?: string | null;
  newVersion?: string | null;
  fileName: string;
  fileUrl?: string | null;
  processType?: string | null;
  platform?: string | null;
  updateResults?: unknown;
}): void {
  const before = buildDeviceOperationBeforeState({ device: params.device });

  logDeviceAudit({
    eventType: 'DEVICE_FIRMWARE_UPDATE_STARTED',
    deviceId: params.deviceIds.length === 1 ? params.deviceIds[0] : 'bulk',
    projectId: params.device.project?.id ?? null,
    projectName: params.device.project?.name ?? null,
    severity: 'WARN',
    summary:
      params.deviceIds.length === 1
        ? `Firmware update to ${params.newVersion ?? 'new version'} started for ${params.deviceIds[0]}.`
        : `Firmware update started for ${params.deviceIds.length} devices.`,
    displayTitle: params.deviceIds.length === 1 ? params.deviceIds[0] : `${params.deviceIds.length} devices`,
    actionDetails: {
      before,
      action: 'FIRMWARE_UPDATE',
      deviceIds: params.deviceIds,
      deviceCount: params.deviceIds.length,
      currentVersion: params.currentVersion ?? params.device.EdgeFWVersion ?? null,
      newVersion: params.newVersion ?? null,
      fileName: params.fileName,
      fileUrl: params.fileUrl ?? null,
      processType: params.processType ?? null,
      platform: params.platform ?? null,
      updateResults: params.updateResults ?? null,
      operations: params.deviceIds.map((deviceId) => ({
        deviceId,
        currentVersion: params.currentVersion ?? params.device.EdgeFWVersion ?? null,
        newVersion: params.newVersion ?? null,
      })),
    },
  });
}
